import React from 'react';
import baseStyles from '../../scss/honeycomb-base.scss.js';
import hcPackage from '../../package.json.js';

const HCR_VERSION_SUFFIX = hcPackage.version.replace(/\./g, '-');
const CLIENT_ID_NAMESPACE = '__honeycomb-react_css_component';
const CLIENT_CLASSNAME_NAMESPACE = 'honeycomb-react-css-tag';
const baseStylesId = "base-styles".concat(CLIENT_ID_NAMESPACE);
const CLIENT_ID_CACHE = {};
const IS_BROWSER = typeof window !== 'undefined' && typeof document !== 'undefined' && document.head;
// generates style tag and appends to head with given id and css as textNode
// insert right after heads open, or right after previous sibling found, if any
const appendStyles = function (id, css) {
  let forceRerender = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var _a;
  if (IS_BROWSER && (forceRerender || !document.getElementById(id))) {
    const node = document.createElement('style');
    node.textContent = css;
    node.className = CLIENT_CLASSNAME_NAMESPACE;
    node.setAttribute('type', 'text/css');
    node.id = id;
    const siblings = document.querySelectorAll(".".concat(CLIENT_CLASSNAME_NAMESPACE));
    if (siblings.length) {
      const child = siblings[siblings.length - 1];
      (_a = child === null || child === void 0 ? void 0 : child.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(node, child.nextSibling);
    } else {
      const {
        firstChild
      } = document.head;
      document.head.insertBefore(node, firstChild);
    }
    return true;
  }
  return false;
};
/**
 * Helper to generate inline style tags and cache css.
 * Puts all the HC-React component styles as a concatenated string into "context.styles",
 * Puts individual style nodes into "context.styleNodes"
 * returns null when no context is provided
 */
const cachedInlineCss = (cache, styles, id) => {
  if (cache && !cache[id]) {
    cache[id] = true;
    if (!cache.styles) cache.styles = '';
    // creating style node tag with the provided styles
    const node = /*#__PURE__*/React.createElement("style", {
      id: id,
      className: CLIENT_CLASSNAME_NAMESPACE,
      type: "text/css",
      dangerouslySetInnerHTML: {
        __html: styles
      }
    });
    cache.styleNodes = !cache.styleNodes ? [] : cache.styleNodes;
    if (id !== baseStylesId) {
      cache.styleNodes.push(node);
      cache.styles += styles;
    } else {
      // pushing base styles to the beginning of the array
      cache.styleNodes.unshift(node);
      // @todo figure out how to handle base style during SSR to avoid specificity issues
      // cache.styles = styles + cache.styles; // pushing base styles to the beginning
    }
  }
  return null;
};
/** Context for server side rendering */
const InjecssContext = /*#__PURE__*/React.createContext(null);
// decorator factory
const Injecss = (WrappedComp, styles) => {
  // create the component
  function EnhancedComp(props) {
    // using displayName and namespace string as unique ID for caching,
    // this ensures every honeycomb component has unique cache ID
    const componentID = WrappedComp.displayName + CLIENT_ID_NAMESPACE + HCR_VERSION_SUFFIX;
    // if no css or empty object return component
    if (typeof styles === 'undefined') {
      return /*#__PURE__*/React.createElement(WrappedComp, Object.assign({}, props));
    }
    // Add base styles, once
    if (!CLIENT_ID_CACHE[baseStylesId] && IS_BROWSER) {
      appendStyles(baseStylesId, baseStyles.raw);
      CLIENT_ID_CACHE[baseStylesId] = true;
    }
    // append cache to head if client and not cached
    if (!CLIENT_ID_CACHE[componentID] && IS_BROWSER) {
      if (appendStyles(componentID, styles.raw)) {
        // set to true if styles were appended successfully
        CLIENT_ID_CACHE[componentID] = true;
      }
    }
    // server rendered component
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(WrappedComp, Object.assign({}, props)), /*#__PURE__*/React.createElement(InjecssContext.Consumer, null, value => {
      cachedInlineCss(value, styles.raw, componentID);
      cachedInlineCss(value, baseStyles.raw, baseStylesId);
      return null;
    }));
  }
  // assign it a name
  EnhancedComp.displayName = "Injecss(".concat(WrappedComp.displayName, ")");
  // ship it
  return EnhancedComp;
};

export { CLIENT_CLASSNAME_NAMESPACE, CLIENT_ID_NAMESPACE, HCR_VERSION_SUFFIX, IS_BROWSER, Injecss, InjecssContext, appendStyles, cachedInlineCss };
